import React from 'react';
import { Helmet } from 'react-helmet-async';
import { City } from '@/data/cities';
import { CitySpecialty } from '@/data/citySpecialties';
import { CityLanguage } from '@/context/languages/languageData';
import { generateAllSpecialtySchemas } from '@/utils/seo/specialtySchemaGenerator';
import { getLocalizedSpecialtyPath } from '@/utils/cityLanguage/getLocalizedSpecialtyPath';
import { getSpecialtyNameForLanguage } from '@/utils/cityLanguage/getSpecialtySlugForLanguage';

interface CitySpecialtySEOProps {
  city: City;
  specialty: CitySpecialty;
  language: CityLanguage;
  title: string;
  metaDescription: string;
  faqs: { question: string; answer: string }[];
}

const CitySpecialtySEO: React.FC<CitySpecialtySEOProps> = ({
  city,
  specialty,
  language,
  title,
  metaDescription,
  faqs
}) => {
  const baseUrl = 'https://psychotherapist.help';
  const currentPath = getLocalizedSpecialtyPath(city, specialty, language);
  const canonicalUrl = `${baseUrl}${currentPath}`;
  
  // Generate hreflang URLs for all supported languages
  const supportedLanguages: CityLanguage[] = ['en', 'es', 'fr', 'de', 'it', 'pt'];
  const hreflangUrls = supportedLanguages.map(lang => ({
    lang,
    url: `${baseUrl}${getLocalizedSpecialtyPath(city, specialty, lang)}`
  }));

  // Generate structured data
  const schemas = generateAllSpecialtySchemas(city, specialty, faqs, language);
  
  const specialtyName = getSpecialtyNameForLanguage(specialty, language);
  const keywords = specialty.keywords[language] || specialty.keywords.en;

  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <title>{title}</title>
      <meta name="title" content={title} />
      <meta name="description" content={metaDescription} />
      <meta name="keywords" content={keywords.join(', ')} />
      <link rel="canonical" href={canonicalUrl} />

      {/* Open Graph / Facebook */}
      <meta property="og:type" content="website" />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={metaDescription} />
      <meta property="og:image" content={`${baseUrl}/og-image.png`} />
      <meta property="og:site_name" content="Global Therapy Hub" />
      <meta property="og:locale" content={
        language === 'es' ? 'es_ES' :
        language === 'fr' ? 'fr_FR' :
        language === 'de' ? 'de_DE' :
        language === 'it' ? 'it_IT' :
        language === 'pt' ? 'pt_PT' : 'en_US'
      } />

      {/* Twitter */}
      <meta property="twitter:card" content="summary_large_image" />
      <meta property="twitter:url" content={canonicalUrl} />
      <meta property="twitter:title" content={title} />
      <meta property="twitter:description" content={metaDescription} />
      <meta property="twitter:image" content={`${baseUrl}/og-image.png`} />

      {/* Hreflang Tags */}
      {hreflangUrls.map(({ lang, url }) => (
        <link key={lang} rel="alternate" hrefLang={lang} href={url} />
      ))}
      <link rel="alternate" hrefLang="x-default" href={hreflangUrls[0].url} />

      {/* Geo Tags */}
      {city.coordinates && (
        <>
          <meta name="geo.position" content={`${city.coordinates.lat};${city.coordinates.lng}`} />
          <meta name="geo.placename" content={city.name} />
          <meta name="geo.region" content={city.countryCode} />
        </>
      )}

      {/* Structured Data */}
      {schemas.map((schema, index) => (
        <script key={index} type="application/ld+json">
          {JSON.stringify(schema)}
        </script>
      ))}

      {/* Additional SEO Tags */}
      <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />
      <meta name="language" content={
        language === 'es' ? 'Spanish' :
        language === 'fr' ? 'French' :
        language === 'de' ? 'German' :
        language === 'it' ? 'Italian' :
        language === 'pt' ? 'Portuguese' : 'English'
      } />
      <meta name="revisit-after" content="7 days" />
      <meta name="author" content="Global Therapy Hub" />
      <meta name="publisher" content="Global Therapy Hub" />
      <meta name="content-language" content={language} />
      
      {/* Performance and Loading Hints */}
      <link rel="preconnect" href="https://psychotherapist.help" />
      <link rel="dns-prefetch" href="https://psychotherapist.help" />
    </Helmet>
  );
};

export default CitySpecialtySEO;
